<template>
  <CCard class="setting-card paid-leave-rule-card">
    <CCardHeader>
      <div class="section-heading">
        <span class="section-icon"><CIcon name="cil-calendar"/></span>
        <div>
          <strong>有休付与ルール</strong>
          <p>{{ summary }}</p>
        </div>
        <SettingSectionAction
          v-if="compact"
          :to="{ name: '有休付与ルールの表' }"
          :label="canEdit ? '変更' : '表を見る'"
        />
      </div>
    </CCardHeader>

    <CCardBody>
      <CAlert v-if="errorMessage" color="danger">{{ errorMessage }}</CAlert>

      <div v-if="loading" class="plr-loading">読み込んでいます…</div>

      <template v-else>
        <!--
          **読んでいる値がどこから来たのかを書く。**
          直書きだったころは、会社が日数を変えても法定表のまま出ていた。
          画面が嘘をついていることに誰も気づけなかった。
        -->
        <CAlert v-if="hasCustomDays" color="info" class="mb-3">
          この会社は<b>法定を上回る日数</b>を設定しています。
          下の表は実際に付与される日数です。
        </CAlert>

        <div
          v-for="row in rows"
          :key="row.workingDays"
          class="paid-rule-block"
        >
          <h3>
            {{ row.label.title }}
            <small v-if="row.label.note">{{ row.label.note }}</small>
          </h3>
          <div class="paid-rule-table-shell">
            <table class="table table-bordered">
              <tr>
                <th class="plr-rowhead">継続勤務年数（年）</th>
                <th
                  v-for="(label, i) in serviceLabels"
                  :key="`head-${row.workingDays}-${i}`"
                  class="plr-colhead text-center"
                >{{ label }}</th>
              </tr>
              <tr>
                <th class="plr-rowhead">付与日数（日）</th>
                <td
                  v-for="(days, i) in row.grantedDays"
                  :key="`days-${row.workingDays}-${i}`"
                  class="text-center"
                  :class="{ 'plr-above': isAbove(days, row.minimumDays[i]) }"
                >
                  {{ display(days) }}
                  <small
                    v-if="isAbove(days, row.minimumDays[i])"
                    class="plr-minimum"
                  >法定 {{ row.minimumDays[i] }}</small>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <p class="text-muted small mb-0">
          法定の下限は労働基準法39条1項・3項（比例付与は労基則24条の3）。
          <b>下回る日数は保存できません。</b>
        </p>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import SettingSectionAction from './SettingSectionAction.vue'
import api from '@/services/companySettings'
import { safeErrorDigest } from '@/utils/safeResponseMessage'
import { SERVICE_LABELS, toDisplayRows } from './paidGrantRuleTable'

/**
 * 会社の有休付与ルールを出す。
 *
 * <h2>直書きをやめた理由</h2>
 *
 * ここは法定表を <code>data()</code> に35個の数字で持っていた。
 * ところが<b>付与の計算は最初から t_paid_grant_rule を引いていて</b>、
 * 会社ごとに違う日数を持てる。会社が上乗せすると、
 * <b>画面は法定表のまま、実際には別の日数が付く。</b>
 * 画面が嘘をついていることに、次の付与月まで誰も気づけない。
 *
 * <h2>一覧でも表を出す</h2>
 *
 * 一度は見出しだけにたたんだが、それだと
 * <b>「法律で決まっている日数のとおりです。」しか出ない。</b>
 * 何日なのかを知るには、別の画面を開くしかない。
 *
 * <b>2.5年目の週4勤務が何日かを、全員が覚えている前提にしない。</b>
 * 弁護士でも社労士でもない人が使う画面で、法定表を暗記させるのは
 * おかしい。見に来た理由はたいてい「うちは何日なのか」なので、
 * <b>それをその場で出す。</b>
 *
 * 見出しの1行は残す。表を読む前に「法定どおりか、上乗せしているか」
 * が分かるほうが速い。{@code compact} は入口のボタンを出すかどうか
 * だけの意味になった。
 */
export default {
  name: 'PaidLeaveGrantRules',

  components: { SettingSectionAction },

  props: {
    compact: { type: Boolean, default: false }
  },

  data () {
    return {
      serviceLabels: SERVICE_LABELS,
      rows: [],
      loading: true,
      errorMessage: ''
    }
  },

  computed: {
    /**
     * 表の上に出す1行。
     *
     * **表を読む前に、法定どおりか上乗せかが分かる。**日数そのものは
     * 下の表にあるので、ここでは「どちらか」だけを言う。
     */
    summary () {
      if (this.loading) return '読み込んでいます…'
      if (this.errorMessage) return '読み込めませんでした。'
      return this.hasCustomDays
        ? '法定を上回る日数を設定しています。'
        : '法律で決まっている日数のとおりです。'
    },

    /** 法定を上回る区分があるか。 */
    hasCustomDays () {
      return this.rows.some(row =>
        row.grantedDays.some((days, i) => this.isAbove(days, row.minimumDays[i])))
    },

    /**
     * 直せる人か。
     *
     * この画面はもともと自社設定の中にあり、SYSTEM_SETTING_MANAGE を
     * 持つ人だけが開ける。**そこは動かしていない**ので、開けた時点で
     * 直せる。読み込めているかどうかだけを見る。
     */
    canEdit () {
      return !this.errorMessage
    }
  },

  created () {
    this.load()
  },

  methods: {
    async load () {
      this.loading = true
      this.errorMessage = ''
      try {
        const data = await api.getPaidGrantRules()
        this.rows = toDisplayRows(data && data.rows, data && data.statutory_minimum)
      } catch (error) {
        this.errorMessage = '有休付与ルールを読み込めませんでした。'
          + ' 時間をおいて開き直してください。'
        this.$emit('load-error', safeErrorDigest(error))
      } finally {
        this.loading = false
      }
    },

    /** 法定を上回っているか。**下回りは出さない**（保存で断っている）。 */
    isAbove (days, minimum) {
      if (days === null || days === undefined || minimum === null
          || minimum === undefined) {
        return false
      }
      return Number(days) > Number(minimum)
    },

    display (days) {
      return days === null || days === undefined ? '—' : days
    }
  }
}
</script>

<style scoped>
.setting-card { min-width: 0; margin-bottom: 18px; }
.section-heading { display: flex; align-items: flex-start; gap: 12px; width: 100%; }
.section-heading strong { color: #17384d; font-size: 16px; }
.section-heading p { margin: 3px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }
.section-icon { display: inline-flex; width: 38px; height: 38px; flex: 0 0 auto; align-items: center; justify-content: center; border-radius: 12px; background: #e9f4fb; color: #0a6ca8; }

.plr-loading { color: #5c6873; }

.paid-rule-block + .paid-rule-block { margin-top: 22px; }
.paid-rule-block h3 { margin: 0 0 10px; color: #314f62; font-size: 14px; }
.paid-rule-block h3 small { margin-left: 8px; color: #7b8e9b; font-weight: 400; font-size: 12px; }

.paid-rule-table-shell { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
.paid-rule-table-shell table { width: 100%; min-width: 680px; margin-bottom: 0; }

.plr-rowhead { background: #edf4f8; color: #28495d; font-weight: 800; white-space: nowrap; }
.plr-colhead { background: #f5f9fb; color: #36576b; white-space: nowrap; }

/* 法定を上回っている区分は、そうと分かるようにする。 */
.plr-above { background: #f2faf4; font-weight: 700; }
.plr-minimum { display: block; color: #6a7f8c; font-size: 10px; font-weight: 400; }

@media (max-width: 767.98px) {
  .paid-rule-table-shell { margin-right: -2px; margin-left: -2px; }
  .section-heading p { overflow-wrap: anywhere; }
}
</style>
